import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Save, Users } from "lucide-react";
import api from "../../lib/api";
import { formatDay } from "../../lib/constants";
import { TeamLogo } from "../../components/UIBits";

export default function AdminScores() {
    const [games, setGames] = useState([]);
    const [teams, setTeams] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/games").then(r => setGames(r.data));
    useEffect(() => {
        load();
        api.get("/teams").then(r => setTeams(r.data));
    }, []);

    const teamMap = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);

    const update = async (gn, payload) => {
        await api.patch(`/games/${gn}/score`, payload);
        load();
    };

    const filtered = games.filter(g => filter === "all" ? true : g.status === filter);

    return (
        <div data-testid="admin-scores">
            <h1 className="font-display text-4xl uppercase tracking-widest mb-6">Scores & parties</h1>
            <div className="flex flex-wrap gap-2 mb-4">
                {["all", "scheduled", "live", "final"].map(s => (
                    <button key={s} onClick={() => setFilter(s)} className={`px-4 py-1.5 angle-tag font-display text-sm tracking-widest uppercase ${filter === s ? "bg-[#7FD93C] text-black" : "bg-white/5 text-white/70"}`}>{s === "all" ? "Tout" : s}</button>
                ))}
            </div>
            <div className="grid gap-3">
                {filtered.map(g => (
                    <div key={g.game_num} className="glass-card p-4" data-testid={`score-row-${g.game_num}`}>
                        <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
                            <div>
                                <div className="text-xs text-white/50 font-mono">#{g.game_num} • {g.category} • {formatDay(g.day)} • {g.time} • {g.ice}</div>
                                <div className="text-xs label-mini mt-1">{g.phase !== "preliminaire" ? g.phase.replace("_", " ").toUpperCase() : "Préliminaire"}</div>
                            </div>
                            <select value={g.status} onChange={e => update(g.game_num, { status: e.target.value })} className="input-field max-w-[180px]" data-testid={`score-status-${g.game_num}`}>
                                <option value="scheduled">À venir</option>
                                <option value="live">EN DIRECT</option>
                                <option value="final">Final</option>
                            </select>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {[
                                { side: "away", name: teamMap[g.away_team_id]?.name || g.away_placeholder, team: teamMap[g.away_team_id], score: g.away_score, pim: g.away_pim, key: "away_score", pkey: "away_pim" },
                                { side: "home", name: teamMap[g.home_team_id]?.name || g.home_placeholder, team: teamMap[g.home_team_id], score: g.home_score, pim: g.home_pim, key: "home_score", pkey: "home_pim" },
                            ].map(s => (
                                <div key={s.side} className="bg-black/30 p-3 flex flex-wrap items-center gap-3">
                                    <TeamLogo team={s.team} size={32} />
                                    <div className="flex-1 min-w-[120px] truncate">{s.name}</div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => update(g.game_num, { [s.key]: Math.max(0, s.score - 1) })} className="btn-ghost p-2" data-testid={`btn-minus-${s.side}-${g.game_num}`}><Minus size={12} /></button>
                                        <input type="number" min="0" className="input-field w-20 text-center font-display text-xl" value={s.score} onChange={e => update(g.game_num, { [s.key]: parseInt(e.target.value || 0, 10) })} />
                                        <button onClick={() => update(g.game_num, { [s.key]: s.score + 1 })} className="btn-ghost p-2" data-testid={`btn-plus-${s.side}-${g.game_num}`}><Plus size={12} /></button>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="label-mini">PIM</span>
                                        <input type="number" min="0" className="input-field w-16 text-center" value={s.pim || 0} onChange={e => update(g.game_num, { [s.pkey]: parseInt(e.target.value || 0, 10) })} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Link to={`/admin/lineup/${g.game_num}`} className="btn-ghost flex items-center gap-2" data-testid={`btn-lineup-${g.game_num}`}><Users size={12} /> Alignement & Stats</Link>
                            <Link to={`/match/${g.game_num}`} className="btn-ghost">Voir page match</Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
